import {
  CalendarDays,
  Clock,
  MapPin,
  Activity,
  Pill,
  Image as ImageIcon,
  AlertTriangle,
  Stethoscope,
  CheckCircle2,
  Circle,
  Sparkles,
  Timer,
  Clock4,
} from "lucide-react";

const CHECK_LABELS = {
  meals: "Meals",
  medication: "Medication",
  mobility: "Mobility",
  hygiene: "Hygiene",
  behavior: "Behavior",
  hydration: "Hydration",
};

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "";
  }
}

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
}

function Section({ icon: Icon, title, count, children, testid }) {
  return (
    <section className="card-surface p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-clinical-primary" />
        <h3 className="font-heading text-sm font-semibold tracking-tight">{title}</h3>
        {typeof count === "number" && (
          <span className="ml-auto label-eyebrow">{count}</span>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * Comprehensive single-shift report view.
 * @param {{ data: any, getAttachmentUrl?: (id: string) => string | null }} props
 *  - getAttachmentUrl: returns a signed URL for inline image preview (admin-only).
 */
export default function ReportFullView({ data, getAttachmentUrl }) {
  if (!data?.report) return null;
  const r = data.report;
  const client = data.client || {};
  const cg = data.caregiver || {};
  const sc = data.shift_clock || null;
  const vitals = data.vitals || [];
  const mar = data.mar_entries || [];
  const atts = data.attachments || [];
  const late = data.late_arrivals || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <header className="card-surface p-5" data-testid="report-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="label-eyebrow">Shift report</div>
            <h2 className="mt-1 font-heading text-2xl font-semibold text-clinical-text">
              {client.name}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-clinical-subtext">
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-3.5 w-3.5" /> {r.shift_date} ·{" "}
                <span className="capitalize">{r.shift_type}</span>
              </span>
              <span>by {cg.name || "—"}</span>
              {client.dob && <span>DOB {client.dob}</span>}
              {client.address && (
                <span className="inline-flex items-center gap-1 max-w-xs truncate">
                  <MapPin className="h-3 w-3" /> {client.address}
                </span>
              )}
            </div>
          </div>
          {r.incident_flag && (
            <span className="pill !bg-destructive/10 !text-destructive">
              <AlertTriangle className="h-3 w-3" /> Incident flagged
            </span>
          )}
        </div>
        {client.notes && (
          <div className="mt-3 rounded-lg border border-clinical-border bg-clinical-bg px-3 py-2 text-xs text-clinical-subtext">
            <span className="font-medium text-clinical-text">Care plan notes: </span>
            {client.notes}
          </div>
        )}
      </header>

      {/* Clinical note */}
      <Section icon={Sparkles} title="Clinical note (AI-formatted)" testid="section-clinical">
        <div className="whitespace-pre-wrap rounded-lg border border-clinical-border bg-clinical-bg p-4 text-sm leading-relaxed text-clinical-text">
          {r.formatted_notes || r.raw_notes || (
            <i className="text-clinical-subtext">No notes</i>
          )}
        </div>
        {r.raw_notes && r.formatted_notes && r.raw_notes !== r.formatted_notes && (
          <details className="mt-2 rounded-lg border border-clinical-border bg-white p-3 text-xs">
            <summary className="cursor-pointer text-clinical-subtext">
              Show original caregiver note
            </summary>
            <div className="mt-2 whitespace-pre-wrap text-clinical-subtext">
              {r.raw_notes}
            </div>
          </details>
        )}
      </Section>

      {/* Checklist */}
      <Section icon={CheckCircle2} title="Care checklist" testid="section-checklist">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(CHECK_LABELS).map(([k, label]) => {
            const on = !!(r.checklist || {})[k];
            return (
              <div
                key={k}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  on
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-clinical-border bg-clinical-bg text-clinical-subtext"
                }`}
                data-testid={`check-${k}`}
              >
                {on ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Circle className="h-4 w-4 text-clinical-subtext/60" />
                )}
                {label}
              </div>
            );
          })}
        </div>
      </Section>

      {/* EVV Clock */}
      {sc && (
        <Section icon={Clock} title="EVV · Clock in / out" testid="section-evv">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="label-eyebrow">Clocked in</div>
              <div className="mt-1 font-medium">{fmtDateTime(sc.clock_in_at)}</div>
              {sc.clock_in_lat != null && (
                <div className="mt-0.5 text-[11px] text-clinical-subtext flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> geo-verified
                </div>
              )}
            </div>
            <div>
              <div className="label-eyebrow">Clocked out</div>
              <div className="mt-1 font-medium">
                {sc.clock_out_at ? fmtDateTime(sc.clock_out_at) : "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow">Duration</div>
              <div className="mt-1 font-medium inline-flex items-center gap-1">
                <Timer className="h-3.5 w-3.5 text-clinical-subtext" />
                {sc.duration_minutes != null
                  ? `${sc.duration_minutes} min · ${(sc.duration_minutes / 60).toFixed(2)} h`
                  : "—"}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Vitals */}
      {vitals.length > 0 && (
        <Section icon={Activity} title="Vitals" count={vitals.length} testid="section-vitals">
          <div className="overflow-x-auto -mx-4">
            <table className="min-w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-clinical-subtext">
                <tr className="border-b border-clinical-border">
                  <th className="text-left px-4 py-2 font-medium">Time</th>
                  <th className="text-left px-4 py-2 font-medium">BP</th>
                  <th className="text-left px-4 py-2 font-medium">Pulse</th>
                  <th className="text-left px-4 py-2 font-medium">Temp</th>
                  <th className="text-left px-4 py-2 font-medium">SpO₂</th>
                  <th className="text-left px-4 py-2 font-medium">Glucose</th>
                  <th className="text-left px-4 py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {vitals.map((v) => (
                  <tr key={v.id} className="border-b border-clinical-border/50">
                    <td className="px-4 py-2 text-clinical-subtext">{fmtTime(v.recorded_at)}</td>
                    <td className="px-4 py-2 font-medium">
                      {v.systolic && v.diastolic ? `${v.systolic}/${v.diastolic}` : "—"}
                    </td>
                    <td className="px-4 py-2">{v.pulse || "—"}</td>
                    <td className="px-4 py-2">{v.temperature_f ? `${v.temperature_f}°F` : "—"}</td>
                    <td className="px-4 py-2">{v.o2_sat ? `${v.o2_sat}%` : "—"}</td>
                    <td className="px-4 py-2">{v.blood_glucose || "—"}</td>
                    <td className="px-4 py-2 text-clinical-subtext">{v.notes || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* MAR */}
      {mar.length > 0 && (
        <Section icon={Pill} title="Medications administered" count={mar.length} testid="section-mar">
          <ul className="divide-y divide-clinical-border" data-testid="mar-list">
            {mar.map((e) => {
              const status = (e.status || "").toLowerCase();
              const pillCls =
                status === "given"
                  ? "bg-emerald-100 text-emerald-800"
                  : status === "refused"
                  ? "bg-destructive/10 text-destructive"
                  : status === "hold"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-clinical-bg text-clinical-subtext";
              return (
                <li key={e.id} className="py-2.5 flex items-center gap-3" data-testid={`mar-${e.id}`}>
                  <div className="text-xs text-clinical-subtext w-12 shrink-0">
                    {fmtTime(e.given_at)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-clinical-text truncate">
                      {e.med_name || "—"}
                    </div>
                    <div className="text-[11px] text-clinical-subtext">
                      {[e.med_dosage, e.med_route].filter(Boolean).join(" · ") || "—"}
                      {e.notes && <> · {e.notes}</>}
                    </div>
                  </div>
                  <span className={`pill ${pillCls} uppercase text-[10px]`}>
                    {status || "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* Attachments */}
      {atts.length > 0 && (
        <Section icon={ImageIcon} title="Photo attachments" count={atts.length} testid="section-attachments">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {atts.map((a) => {
              const url = getAttachmentUrl ? getAttachmentUrl(a.id) : null;
              return (
                <div
                  key={a.id}
                  className="rounded-lg border border-clinical-border overflow-hidden bg-clinical-bg"
                  data-testid={`attachment-${a.id}`}
                >
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={url}
                        alt={a.filename || "photo"}
                        className="h-32 w-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <div className="h-32 flex items-center justify-center text-clinical-subtext">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                  )}
                  <div className="px-2 py-1.5 text-[10px] text-clinical-subtext truncate">
                    {a.filename || "photo"} · {Math.round((a.size_bytes || 0) / 1024)} KB
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Late arrivals */}
      {late.length > 0 && (
        <Section icon={Clock4} title="Late-arrival pings" count={late.length} testid="section-late">
          <ul className="divide-y divide-clinical-border">
            {late.map((l) => (
              <li key={l.id} className="py-2 text-sm">
                <div className="flex items-center gap-2 text-clinical-text">
                  <span className="text-xs text-clinical-subtext w-32">{fmtDateTime(l.created_at)}</span>
                  {l.eta_minutes != null && <span className="font-medium">ETA {l.eta_minutes} min</span>}
                  {l.acknowledged && (
                    <span className="ml-auto pill !bg-emerald-100 !text-emerald-800 uppercase text-[10px]">
                      acknowledged
                    </span>
                  )}
                </div>
                {l.note && <div className="mt-0.5 text-[11px] text-clinical-subtext">{l.note}</div>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Quick contacts (compact) */}
      {(client.doctor_phone || client.family_contact_phone || client.emergency_note) && (
        <Section icon={Stethoscope} title="Client quick contacts" testid="section-contacts">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {client.doctor_phone && (
              <div className="rounded-lg border border-clinical-border bg-white px-3 py-2">
                <div className="text-[11px] text-clinical-subtext">Doctor · {client.doctor_name || "—"}</div>
                <div className="font-medium">{client.doctor_phone}</div>
              </div>
            )}
            {client.family_contact_phone && (
              <div className="rounded-lg border border-clinical-border bg-white px-3 py-2">
                <div className="text-[11px] text-clinical-subtext">
                  Family · {client.family_contact_name || "—"}
                </div>
                <div className="font-medium">{client.family_contact_phone}</div>
              </div>
            )}
          </div>
          {client.emergency_note && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {client.emergency_note}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

export { CHECK_LABELS, fmtTime, fmtDateTime };
